import {
  adminStreamerSchema,
  type AdminStreamer,
  type StreamerStatus,
} from '@growup/streaming-shared';
import {
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  Table,
  Textarea,
  type Column,
} from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';
import { SubNav } from '../members/MembersSection';
import { formatDate, formatDateTime } from '../format';
import { matchesQuery } from '../quickFilter';
import { AddExistingStreamer } from './AddExistingStreamer';
import { ChecklistPanel } from './ChecklistPanel';
import { CheckupHistory } from './CheckupView';
import { ConversationView } from './ConversationView';
import { SemaforoBadges } from './SemaforoBadges';
import { StreamerDashboardCard } from './StreamerDashboardCard';
import { StreamerHistoryCard } from './StreamerHistoryCard';
import { formatCount, formatMinutes, LEVEL_LABEL, STREAMER_LOOK } from './labels';

const COLUMNS: Column<AdminStreamer>[] = [
  {
    key: 'name',
    header: 'Streamer',
    cell: (s) => (
      <span className="flex flex-col">
        <span className="font-semibold">{s.displayName}</span>
        <span className="text-caption text-gu-ink-muted">
          twitch.tv/{s.channel} · {s.login}
        </span>
      </span>
    ),
  },
  {
    key: 'semaforo',
    header: 'Semáforo',
    cell: (s) => <SemaforoBadges semaforo={s.semaforo} />,
  },
  {
    key: 'live',
    header: 'Agora',
    hideOnMobile: true,
    cell: (s) =>
      s.live ? (
        <StatusBadge tone="success" symbol="●">
          No ar · {formatCount(s.live.viewers)}
        </StatusBadge>
      ) : (
        <span className="text-gu-ink-muted">—</span>
      ),
  },
  {
    key: 'hours',
    header: 'Em direto (30 dias)',
    align: 'right',
    cell: (s) => `${formatMinutes(s.last30.minutesLive)} · ${String(s.last30.sessions)}×`,
  },
  {
    key: 'viewers',
    header: 'Média · pico',
    align: 'right',
    hideOnMobile: true,
    cell: (s) => `${formatCount(s.last30.avgViewers)} · ${formatCount(s.last30.peakViewers)}`,
  },
  {
    key: 'followers',
    header: 'Seguidores',
    align: 'right',
    hideOnMobile: true,
    cell: (s) =>
      s.last30.followers === null
        ? '—'
        : `${formatCount(s.last30.followers)}${
            s.last30.followersChange
              ? ` (${s.last30.followersChange > 0 ? '+' : ''}${formatCount(s.last30.followersChange)})`
              : ''
          }`,
  },
  {
    key: 'status',
    header: 'Estado',
    align: 'right',
    cell: (s) =>
      s.inactive ? (
        <StatusBadge tone="warning">Inativo · {s.daysWithoutStream} dias</StatusBadge>
      ) : (
        <StatusBadge tone={STREAMER_LOOK[s.status].tone}>
          {STREAMER_LOOK[s.status].label}
        </StatusBadge>
      ),
  },
];

type Show = 'all' | 'red' | 'noContact' | 'overdue' | 'inactive' | 'community';

const SHOW: Record<Exclude<Show, 'all'>, (s: AdminStreamer) => boolean> = {
  red: (s) => s.semaforo?.activity === 'red',
  noContact: (s) => s.semaforo?.noContact === true,
  overdue: (s) => s.semaforo?.checkupOverdue === true,
  inactive: (s) => s.inactive,
  community: (s) => s.canMoveToCommunity,
};

/**
 * The team, with the last 30 days from Twitch; one streamer opens its details.
 * `openAccountId` opens that account's streamer straight away (from a member record).
 */
export function StreamersView({
  openAccountId = null,
  onOpenMember,
}: {
  openAccountId?: string | null;
  onOpenMember?: ((accountId: string) => void) | undefined;
}) {
  const [list, setList] = useState<AdminStreamer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const [show, setShow] = useState<Show>('all');
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson('/api/streaming/streamers', z.array(adminStreamerSchema))
      .then((rows) => {
        setList(rows);
        if (openAccountId !== null) {
          const wanted = rows.find((s) => s.accountId === openAccountId);
          if (wanted) setSelected(wanted.id);
        }
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [openAccountId]);
  useEffect(load, [load]);

  const streamer = list?.find((s) => s.id === selected) ?? null;
  if (streamer) {
    return (
      <StreamerDetail
        streamer={streamer}
        onBack={() => setSelected(null)}
        onOpenMember={onOpenMember}
        onSaved={(saved) =>
          setList((all) => all?.map((s) => (s.id === saved.id ? saved : s)) ?? null)
        }
      />
    );
  }

  const rows = (list ?? []).filter(
    (s) =>
      (showRemoved || s.status !== 'removed') &&
      (show === 'all' || SHOW[show](s)) &&
      matchesQuery(query, s.login, s.channel, s.email),
  );
  const active = (list ?? []).filter((s) => s.status === 'active');
  const inactive = active.filter((s) => s.inactive).length;
  const toCommunity = active.filter((s) => s.canMoveToCommunity).length;
  const live = active.filter((s) => s.live !== null).length;
  const minutes = active.reduce((sum, s) => sum + s.last30.minutesLive, 0);

  return (
    <Card
      headingLevel={2}
      title="Streamers"
      actions={
        adding ? null : (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            Adicionar streamer da equipa
          </Button>
        )
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {adding ? (
        <AddExistingStreamer
          onAdded={(added) =>
            setList((all) => [...(all ?? []).filter((s) => s.id !== added.id), added])
          }
          onClose={() => setAdding(false)}
        />
      ) : null}
      {list ? (
        <p className="text-gu-ink-muted">
          {active.length} ativos · {live} no ar agora · {formatMinutes(minutes)} em direto nos
          últimos 30 dias
          {inactive > 0
            ? ` · ${String(inactive)} inativos (60 dias ou mais sem transmitir)${
                toCommunity > 0 ? `, ${String(toCommunity)} já podem passar a comunidade` : ''
              }`
            : ''}
        </p>
      ) : null}
      <Input
        type="search"
        aria-label="Procurar streamer"
        placeholder="Procurar login, canal ou email"
        className="max-w-xs"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Mostrar">
          <Select value={show} onChange={(e) => setShow(e.target.value as Show)}>
            <option value="all">Todos</option>
            <option value="red">Vermelho (mais de 30 dias sem transmitir)</option>
            <option value="noContact">Sem contacto do padrinho (30+ dias)</option>
            <option value="overdue">Relatório do padrinho em atraso</option>
            <option value="inactive">Inativos (60+ dias sem transmitir)</option>
            <option value="community">Podem passar a comunidade (90+ dias)</option>
          </Select>
        </Field>
        <Checkbox
          label="Mostrar quem saiu"
          checked={showRemoved}
          onChange={(e) => setShowRemoved(e.target.checked)}
        />
      </div>
      <Table
        caption="Streamers"
        columns={[
          ...COLUMNS,
          {
            key: 'open',
            header: '',
            align: 'right',
            cell: (s) => (
              <Button variant="ghost" size="sm" onClick={() => setSelected(s.id)}>
                Abrir
              </Button>
            ),
          },
        ]}
        rows={rows}
        rowKey={(s) => s.id}
        empty={list ? 'Ainda não há streamers. Aprove uma candidatura.' : 'A carregar…'}
      />
    </Card>
  );
}

type DetailTab = 'resumo' | 'checklist' | 'numeros' | 'acompanhamento' | 'mensagens';

/**
 * A ficha de um streamer na gestao, em separadores (arrumar a gestao, parte
 * 4): o cabecalho fica sempre; Resumo (estado, notas, comunidade), Primeiros
 * 30 dias, Numeros, Acompanhamento (relatorios do padrinho) e Mensagens.
 */
function StreamerDetail({
  streamer: s,
  onBack,
  onOpenMember,
  onSaved,
}: {
  streamer: AdminStreamer;
  onBack: () => void;
  onOpenMember?: ((accountId: string) => void) | undefined;
  onSaved: (s: AdminStreamer) => void;
}) {
  const [status, setStatus] = useState<StreamerStatus>(s.status);
  const [notes, setNotes] = useState(s.notes);
  const [photoHidden, setPhotoHidden] = useState(s.photoHidden);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'danger' | 'success'; text: string } | null>(null);
  const [farewell, setFarewell] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [view, setView] = useState<DetailTab>('resumo');

  const moveToCommunity = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const saved = adminStreamerSchema.parse(
        await send('POST', `/api/streaming/streamers/${s.id}/community`, { note: farewell }),
      );
      onSaved(saved);
      setStatus(saved.status);
      setConfirming(false);
      setMessage({ tone: 'success', text: 'Passou a comunidade. Recebeu um email.' });
    } catch (e) {
      setMessage({ tone: 'danger', text: messageOf(e) });
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const saved = adminStreamerSchema.parse(
        await send('PUT', `/api/streaming/streamers/${s.id}`, { status, notes, photoHidden }),
      );
      onSaved(saved);
      setMessage({ tone: 'success', text: 'Guardado.' });
    } catch (e) {
      setMessage({ tone: 'danger', text: messageOf(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Button variant="ghost" size="sm" className="self-start" onClick={onBack}>
        ← Streamers
      </Button>
      <header className="flex flex-col gap-1">
        <p className="font-sub text-caption font-bold uppercase text-gu-ink-muted">
          {`Na equipa desde ${formatDateTime(s.joinedAt)}${
            s.origin === 'existing' ? ' · adicionado pelo staff' : ''
          }`}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-display text-headline">{s.displayName}</h2>
          <StatusBadge tone={STREAMER_LOOK[s.status].tone}>
            {STREAMER_LOOK[s.status].label}
          </StatusBadge>
        </div>
        <p className="break-words text-gu-ink-muted">
          <a
            className="gu-link"
            href={`https://www.twitch.tv/${s.channel}`}
            target="_blank"
            rel="noreferrer"
          >
            twitch.tv/{s.channel}
          </a>{' '}
          · conta {s.login}
          {s.email ? ` · ${s.email}` : ''}
          {s.level ? ` · ${LEVEL_LABEL[s.level] ?? s.level}` : ''}
          {' · '}
          {s.padrinho
            ? `${s.padrinho.confirmed ? 'Padrinho' : 'Padrinho provisório'}: ${s.padrinho.login}`
            : 'Sem padrinho (atribua em Padrinhos)'}
        </p>
      </header>

      <SubNav
        label={`Ficha de ${s.displayName}`}
        tabs={[
          { id: 'resumo' as const, label: 'Resumo' },
          { id: 'checklist' as const, label: 'Primeiros 30 dias' },
          { id: 'numeros' as const, label: 'Números' },
          { id: 'acompanhamento' as const, label: 'Acompanhamento' },
          ...(s.padrinho ? [{ id: 'mensagens' as const, label: 'Mensagens' }] : []),
        ]}
        current={view}
        onChange={setView}
      />

      {view === 'resumo' ? (
        <Card headingLevel={2} title="Estado e notas">
          <p className="text-gu-ink-muted">
            Última transmissão:{' '}
            {s.lastStreamAt ? formatDate(s.lastStreamAt) : 'nenhuma desde que entrou no site'}
            {s.inactive ? ` · inativo há ${String(s.daysWithoutStream)} dias` : ''}
          </p>
          {s.leftReason === 'inactivity' && s.status === 'removed' ? (
            <Notice tone="info">
              Passou a comunidade por inatividade
              {s.leftAt ? ` em ${formatDate(s.leftAt)}` : ''}. Pode candidatar-se de novo.
            </Notice>
          ) : null}
          {s.canMoveToCommunity ? (
            <section
              aria-label="Passar a comunidade"
              className="flex flex-col gap-3 border border-gu-line p-4"
            >
              <p>
                Não transmite há {s.daysWithoutStream} dias. Pode passar a comunidade: sai da equipa
                de streaming, mas a conta, a carta e o histórico ficam, e pode candidatar-se de
                novo. Recebe um email.
              </p>
              {confirming ? (
                <>
                  <Field label="Nota no email" optional>
                    <Textarea
                      rows={2}
                      maxLength={1000}
                      value={farewell}
                      onChange={(e) => setFarewell(e.target.value)}
                    />
                  </Field>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" disabled={busy} onClick={() => void moveToCommunity()}>
                      Confirmar: passar a comunidade
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                      Cancelar
                    </Button>
                  </div>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  className="self-start"
                  onClick={() => setConfirming(true)}
                >
                  Passar a comunidade
                </Button>
              )}
            </section>
          ) : null}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[14rem_minmax(0,1fr)]">
            <Field label="Estado" hint="Em pausa ou fora: sai do portal e dos eventos.">
              <Select value={status} onChange={(e) => setStatus(e.target.value as StreamerStatus)}>
                {(Object.keys(STREAMER_LOOK) as StreamerStatus[]).map((k) => (
                  <option key={k} value={k}>
                    {STREAMER_LOOK[k].label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Notas internas" optional>
              <Textarea
                rows={3}
                maxLength={2000}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Field>
          </div>
          <Checkbox
            label={
              s.showPhoto
                ? 'Esconder a foto de perfil em growup.gg/streamers (fica a da Twitch)'
                : 'Esconder a foto de perfil em growup.gg/streamers (o streamer ainda não a autorizou)'
            }
            checked={photoHidden}
            onChange={(e) => setPhotoHidden(e.target.checked)}
          />
          {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={busy} onClick={() => void save()}>
              Guardar
            </Button>
            {onOpenMember ? (
              <Button variant="secondary" size="sm" onClick={() => onOpenMember(s.accountId)}>
                Ver ficha de sócio
              </Button>
            ) : null}
          </div>
        </Card>
      ) : null}

      {view === 'numeros' ? (
        <>
          <StreamerDashboardCard streamerId={s.id} />
          <StreamerHistoryCard streamerId={s.id} />
        </>
      ) : null}

      {view === 'checklist' ? (
        <Card headingLevel={2} title="Checklist dos 30 dias">
          <ChecklistPanel base={`/api/streaming/streamers/${s.id}/checklist`} staff />
        </Card>
      ) : null}

      {view === 'acompanhamento' ? (
        <Card headingLevel={2} title="Relatórios do padrinho">
          <CheckupHistory streamerId={s.id} />
        </Card>
      ) : null}

      {view === 'mensagens' && s.padrinho ? (
        <Card
          headingLevel={2}
          title="Mensagens com o padrinho"
          eyebrow="Só leitura: a conversa é entre o streamer e o padrinho."
        >
          <ConversationView path={`/api/streaming/streamers/${s.id}/messages`} canWrite={false} />
        </Card>
      ) : null}
    </div>
  );
}
